import { useContext } from 'react';
import ApiContext from './context';
import TRAINERS from './trainers';
import ATHLETES from './athletes';
import GYMS from './gyms';
import CERTIFICATION from './certification';
import SUPER_MANAGERS from './superManagers';
import MANAGERS from './managers';
import COMMON from './common';
import DANKAI from './dankai';
import GROUPS from './groups';
import CERTIFICATION_ATHLETE from './certifications_athlete';
import CONTRIBUTIONS from './contributions';
import STATS from './stats';

export default function useApi() {
  return useContext(ApiContext);
}

export class MyClubCApi {
  constructor(apolloClient) {
    this.client = apolloClient;
  }

  async getSections() {
    const { data } = await this.client.query({
      query: COMMON.sectionsQuery,
      fetchPolicy: 'network-only',
    });
    return data.sections;
  }

  async getDankais({ type }) {
    const { data } = await this.client.query({
      query: DANKAI.dankaiListQuery,
      variables: { type },
      fetchPolicy: 'network-only',
    });
    return data.dankais;
  }

  async getDankai({ dankaiId }) {
    const { data } = await this.client.query({
      query: DANKAI.dankaiQuery,
      variables: { dankaiId },
      fetchPolicy: 'network-only',
    });
    return data.dankai;
  }

  async updateDankai({ id, inputData }) {
    const { data } = await this.client.mutate({
      mutation: DANKAI.dankaiUpdateMutation,
      variables: { id, inputData },
    });
    return data.updateDankai;
  }

  async generateDankaiPdf({ id }) {
    const { data } = await this.client.mutate({
      mutation: DANKAI.generateDankaiPdfMutation,
      variables: { id },
    });
    return data.generateDankaiPdf;
  }

  async getTrainers({ trainerId, status, gymId, trainerIds }) {
    let userIds;
    if (trainerId) {
      userIds = [trainerId];
    }

    if (trainerIds && trainerIds.length > 0) {
      userIds = trainerIds;
    }
    const { data } = await this.client.query({
      query: TRAINERS.manyQuery,
      variables: {
        inputFilter: { userIds: userIds, status: status, trainerGymId: gymId },
      },
      fetchPolicy: 'network-only',
    });
    return data.trainers;
  }

  async getTrainer(trainerId) {
    const { data } = await this.client.query({
      query: TRAINERS.oneQuery,
      variables: { id: trainerId },
      fetchPolicy: 'network-only',
    });
    return data.trainer;
  }

  async getGyms() {
    const { data } = await this.client.query({
      query: GYMS.manyQuery,
      fetchPolicy: 'network-only',
    });
    return data.gyms;
  }

  async deleteAthlete(athleteId) {
    const { data } = await this.client.mutate({
      mutation: ATHLETES.deleteMutation,
      variables: { id: athleteId },
    });
    return data.deleteAthlete;
  }

  async deleteTrainer(trainerId) {
    const { data } = await this.client.mutate({
      mutation: TRAINERS.deleteMutation,
      variables: { id: trainerId },
    });
    return data.deleteTrainer;
  }

  async getAnalyticGym({ gymId, year, trainerId }) {
    const { data } = await this.client.query({
      query: GYMS.getAnalytic,
      variables: {
        inputFilter: { gymId: gymId, year: year, trainerId: trainerId },
      },
    });
    return data.analyticGym;
  }

  async archivateAthlete(athleteId, reason) {
    const { data } = await this.client.mutate({
      mutation: ATHLETES.archivateMutation,
      variables: { id: athleteId, reason: reason },
    });
    return data.archivateAthlete;
  }

  async activateAthlete(athleteId) {
    const { data } = await this.client.mutate({
      mutation: ATHLETES.activateMutation,
      variables: { id: athleteId },
    });
    return data.activateAthlete;
  }

  async archivateTrainers(trainerId, reason) {
    const { data } = await this.client.mutate({
      mutation: TRAINERS.archivateMutation,
      variables: { id: trainerId, reason: reason },
    });
    return data.archivateTrainer;
  }

  async archivateSuperManager(superManagerId, reason) {
    const { data } = await this.client.mutate({
      mutation: SUPER_MANAGERS.archivateMutation,
      variables: { id: superManagerId, reason: reason },
    });
    return data.archivateSuperManager;
  }

  async getAthlete(athleteId) {
    const { data } = await this.client.query({
      query: ATHLETES.oneQuery,
      variables: { id: athleteId },
      fetchPolicy: 'network-only',
    });
    return data.athlete;
  }

  async getAthletes({ trainerId, mainTrainerId }) {
    const { data } = await this.client.query({
      query: ATHLETES.manyQuery,
      variables: {
        inputFilter: { trainerId: trainerId, mainTrainerId: mainTrainerId },
      },
      fetchPolicy: 'network-only',
    });
    return data.athletes;
  }

  async getPaginatedAthletes({
    limit,
    page,
    status,
    trainerId,
    gymId,
    sectionTypeId,
    query,
  }) {
    const { data } = await this.client.query({
      query: ATHLETES.paginatedAthletesQuery,
      variables: {
        inputFilter: {
          limit: limit,
          page: page,
          status: status,
          trainerId: trainerId,
          sectionId: sectionTypeId,
          gymId: gymId,
          query: query,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.paginatedAthletes;
  }

  async getPaginatedTrainers({ limit, page, status, trainerId, gymId, query }) {
    const { data } = await this.client.query({
      query: TRAINERS.paginatedTrainersQuery,
      variables: {
        inputFilter: {
          limit: limit,
          page: page,
          status: status,
          trainerId: trainerId,
          gymId: gymId,
          query: query,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.paginatedTrainers;
  }

  async getPaginatedSuperManagers({ limit, page, status, query }) {
    const { data } = await this.client.query({
      query: SUPER_MANAGERS.paginatedSuperManagersQuery,
      variables: {
        inputFilter: {
          limit: limit,
          page: page,
          status: status,
          query: query,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.paginatedSuperManagers;
  }

  async getPaginatedManagers({ limit, page, status, query }) {
    const { data } = await this.client.query({
      query: MANAGERS.paginatedManagersQuery,
      variables: {
        inputFilter: {
          limit: limit,
          page: page,
          status: status,
          query: query,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.paginatedManagers;
  }

  async deleteSuperManager(superManagerId) {
    const { data } = await this.client.mutate({
      mutation: SUPER_MANAGERS.deleteMutation,
      variables: { id: superManagerId },
    });
    return data.deleteSuperManager;
  }

  async getPaginatedGroups({
    limit,
    page,
    trainerId,
    gymId,
    sectionTypeId,
    searchQuery,
  }) {
    const { data } = await this.client.query({
      query: GROUPS.paginatedGroupsQuery,
      variables: {
        inputFilter: {
          limit: limit,
          page: page,
          query: searchQuery,
          trainerId: trainerId,
          gymId: gymId,
          section: sectionTypeId,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.paginatedGroups;
  }

  async getGroups({ athleteId, gymIds, trainerId }) {
    const { data } = await this.client.query({
      query: GROUPS.manyQuery,
      variables: {
        inputFilter: {
          athleteId: athleteId,
          gymIds: gymIds,
          trainerId: trainerId,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.groups;
  }

  async addAthleteToGroup({ athleteId, groupId }) {
    const { data } = await this.client.mutate({
      mutation: ATHLETES.addAthleteToGroupMutation,
      variables: { athleteId: athleteId, toGroupId: groupId },
    });
    return data.addAthleteToGroup;
  }

  async deleteGroup(groupId) {
    const { data } = await this.client.mutate({
      mutation: GROUPS.deleteMutation,
      variables: { id: groupId },
    });
    return data.deleteGroup;
  }

  async generateProtocolXls(certificationId) {
    const { data } = await this.client.mutate({
      mutation: CERTIFICATION.generateProtocolXlsMutation,
      variables: { certificationId: certificationId },
    });
    return data.generateProtocolXls;
  }

  async getCertificationAthletes({
    certificationsId,
    contributionStatus,
    contributionYear,
    query,
    trainerId,
  }) {
    const { data } = await this.client.query({
      query: CERTIFICATION_ATHLETE.manyQuery,
      variables: {
        inputFilter: {
          query: query,
          trainerId: trainerId,
          certificationsId: certificationsId,
          contributionStatus: contributionStatus,
          contributionYear: contributionYear,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.certificationsAthletes;
  }

  async deleteCertificationAthlete({ certificationsId }) {
    const { data } = await this.client.mutate({
      mutation: CERTIFICATION_ATHLETE.deleteMutation,
      variables: { id: certificationsId },
    });
    return data.deleteCertificationsAthletes;
  }

  async getContributionUsers({ trainerId, year, gymId }) {
    const { data } = await this.client.query({
      query: CONTRIBUTIONS.contributionUserListQuery,
      variables: {
        inputFilter: {
          trainerId: Number.parseInt(trainerId),
          year: year,
          gymId: gymId,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.contributionUserList;
  }

  async getContributionTrainers({ trainerId, year, gymId }) {
    const { data } = await this.client.query({
      query: CONTRIBUTIONS.contributionTrainerListQuery,
      variables: {
        inputFilter: {
          trainerId: Number.parseInt(trainerId),
          year: year,
          gymId: gymId,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data.contributionTrainerList;
  }

  async getContributionAvailableGyms(athleteIds) {
    const { data } = await this.client.query({
      query: CONTRIBUTIONS.contributionAvailableGymsListQuery,
      variables: { inputFilter: { athleteIds: athleteIds } },
      fetchPolicy: 'network-only',
    });
    return data.contributionAvailableGymsList;
  }

  async getStatsGyms({ year }) {
    const { data } = await this.client.query({
      query: STATS.gymsQuery,
      variables: { inputFilter: { year: year } },
      fetchPolicy: 'network-only',
    });
    return data.statsGyms;
  }

  async getStatsSections({ year }) {
    const { data } = await this.client.query({
      query: STATS.sectionsQuery,
      variables: { inputFilter: { year: year } },
      fetchPolicy: 'network-only',
    });
    return data.statsSections;
  }

  async generateAthletesXls(
    requestID,
    { status, trainerId, gymId, sectionTypeId }
  ) {
    const { data } = await this.client.mutate({
      mutation: ATHLETES.generateAthletesXlsMutation,
      variables: {
        inputData: {
          requestId: requestID,
          status: status,
          trainerId: trainerId,
          gymId: gymId,
          sectionId: sectionTypeId,
        },
      },
    });
    return data.generateAthletesXls;
  }

  async generateAthletesXlsStatus(requestID) {
    const { data } = await this.client.query({
      query: ATHLETES.generateAthletesXlsStatusQuery,
      fetchPolicy: 'network-only',
      variables: { requestId: requestID },
    });
    return data.generateAthletesXlsStatus;
  }
}
